import { Fieldset, FormSection, Layout } from '@vwfs-bronson/bronson-react';
import { Form, Formik, useFormikContext } from 'formik';
import { t } from 'i18next';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ObjectSchema } from 'yup';
import { formAutocomplete } from '../../../config';
import { FormHelpers } from '../../../screens/utils';
import { journeyDataActions } from '../../../services/redux/features/journeyData.slice';
import { AddressSearchFormField } from '../../Fieldset/AddressSearchFormField';
import { FormSectionEditButton } from '../../FormSectionEditButton';
import { FormSectionSubmitButton } from '../../FormSectionSubmitButton';
import { RenderFormSectionSummaryTitle } from '../../FormSectionSummaryTitle';
import { createFormInputs } from './formInputs';
import { AddressFormModel_OL } from './formModel';
import { getInitialValues } from './initialValues';
import { requestMapper } from './requestMapper';
import { getValidationSchema } from './validationSchema';

type Props = {
  formHelpers: FormHelpers;
  titleTranslationKey: `addressForm:title:${string}`;
  showCountry: boolean;
};

export const AddressFormNew_OL: React.FC<Props> = (props) => {
  const { formHelpers, titleTranslationKey, showCountry } = props;

  const [initialValues] = useState(getInitialValues(showCountry));
  const [validationSchema] = useState<ObjectSchema<Partial<AddressFormModel_OL>>>(getValidationSchema(showCountry));

  const { formSectionProps, onFormSubmitSuccess, onStartEditInSummaryMode, isDisabledFormSummary } = formHelpers;

  const onSubmit = (values: AddressFormModel_OL) => {
    /* start loader */
    Promise.resolve(/* submit */)
      .then(() => onFormSubmitSuccess(values))
      .catch(/* on error */)
      .finally(/* end loader */);
  };

  return (
    <FormSection
      title={RenderFormSectionSummaryTitle(t(titleTranslationKey), isDisabledFormSummary)}
      {...formSectionProps}
    >
      <FormSectionEditButton onClick={onStartEditInSummaryMode} formHelpers={formHelpers} />
      <Formik validationSchema={validationSchema} initialValues={initialValues} onSubmit={onSubmit} enableReinitialize>
        {/* eslint-disable-next-line no-use-before-define */}
        <FormContent {...props} showCountry={showCountry} />
      </Formik>
    </FormSection>
  );
};

type FormContentProps = Props & {
  showCountry: boolean;
};

export const FormContent = (props: FormContentProps) => {
  const formTranslationKey = 'addressForm:currentAddressForm';
  const [inputs] = useState(createFormInputs(formTranslationKey));

  const { values, submitForm } = useFormikContext<AddressFormModel_OL>();
  const { formHelpers, showCountry } = props;
  const { onFormValuesChange } = formHelpers;

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(journeyDataActions.updateData(requestMapper(values)));
    onFormValuesChange(values);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, onFormValuesChange]);

  const disableForm = formHelpers.isDisabledFormSummary;

  /* Disable prettier to keep template compact and readable. */
  /* prettier-ignore */
  return (
    <Form autoComplete={formAutocomplete}>
      <Fieldset disabled={disableForm}>
        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/1" s="1/1">
              <AddressSearchFormField />
            </Layout.Item>
          </Layout>
        </Fieldset.Row>

        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/2" s="1/1">{inputs.addressLine1()}</Layout.Item>
            <Layout.Item default="1/2" s="1/1">{inputs.addressLine2()}</Layout.Item>
          </Layout>
        </Fieldset.Row>

        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/2" s="1/1">{inputs.addressLine3()}</Layout.Item>
            <Layout.Item default="1/2" s="1/1">{inputs.town()}</Layout.Item>
          </Layout>
        </Fieldset.Row>

        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/2" s="1/1">{inputs.county()}</Layout.Item>
            <Layout.Item default="1/2" s="1/1">{inputs.eirCode()}</Layout.Item>
          </Layout>
        </Fieldset.Row>

        {showCountry &&
          <Fieldset.Row>
            <Layout>
              <Layout.Item default="1/2" s="1/1">{inputs.country()}</Layout.Item>
            </Layout>
          </Fieldset.Row>
        }

      </Fieldset>
      <FormSectionSubmitButton onClick={submitForm} formHelpers={formHelpers}/>
    </Form>
  );
};
